import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Language } from '../types';
import { getLanguages, isAuthenticated } from '../api';

interface ReferenceDataContextType {
  languages: Language[];
  loading: boolean;
  error: string | null;
  refreshLanguages: () => Promise<void>;
  refreshAfterLogin: () => Promise<void>;
  lastFetch: number | null;
}

const ReferenceDataContext = createContext<ReferenceDataContextType | undefined>(undefined);

export const useReferenceData = () => {
  const context = useContext(ReferenceDataContext);
  if (context === undefined) {
    throw new Error('useReferenceData must be used within a ReferenceDataProvider');
  }
  return context;
};

interface ReferenceDataProviderProps {
  children: ReactNode;
}

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes - languages don't change often

export const ReferenceDataProvider: React.FC<ReferenceDataProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<number | null>(null);
  const initializedRef = useRef(false);
  
  const languagesRef = useRef(languages);
  const lastFetchRef = useRef(lastFetch);
  
  useEffect(() => { languagesRef.current = languages; }, [languages]);
  useEffect(() => { lastFetchRef.current = lastFetch; }, [lastFetch]);

  const refreshLanguages = useCallback(async () => {
    
    
    // Always fetch if we don't have languages (e.g., after page refresh)
    if (languagesRef.current.length === 0) {
      
      // Force refresh
    } else {
      // Don't fetch if we recently fetched and cache is still valid
      const shouldRefresh = () => {
        if (!lastFetchRef.current) return true;
        return Date.now() - lastFetchRef.current > CACHE_DURATION;
      };
      
      if (!shouldRefresh()) {
        
        return;
      }
    }

    
    setLoading(true);
    setError(null);

    try {
      const fetchedLanguages = await getLanguages();
      setLanguages(fetchedLanguages);
      setLastFetch(Date.now());
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('errors.failedToLoadLanguages');
      setError(errorMessage);
      
    } finally {
      setLoading(false);
    }
  }, [t]);

  const refreshAfterLogin = useCallback(async () => {
    
    initializedRef.current = false; // Reset initialization flag
    await refreshLanguages();
  }, [refreshLanguages]);

  // Initial load when provider mounts (with guard against double-mounting in StrictMode)
  useEffect(() => {
    
    if (!initializedRef.current && isAuthenticated()) {
      
      initializedRef.current = true;
      refreshLanguages();
    } else {
      
    }
  }, [refreshLanguages, languages.length]);

  const value: ReferenceDataContextType = {
    languages,
    loading,
    error,
    refreshLanguages,
    refreshAfterLogin,
    lastFetch,
  };

  return <ReferenceDataContext.Provider value={value}>{children}</ReferenceDataContext.Provider>;
};